import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if Android
    const userAgent = navigator.userAgent.toLowerCase();
    const android = /android/i.test(userAgent);
    setIsAndroid(android);

    // Check if dismissed recently (within 7 days)
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const diffDays = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      if (diffDays < 7) {
        return;
      }
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show on Android
      if (android) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Also show for Android users who might have dismissed the native prompt
    if (android && !window.matchMedia('(display-mode: standalone)').matches) {
      // Delay showing the custom prompt
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // If no deferred prompt, show manual instructions
      alert(
        "Pour installer l'application :\n\n" +
        "1. Appuyez sur le menu ⋮ (trois points) en haut à droite\n" +
        "2. Sélectionnez 'Ajouter à l'écran d'accueil'\n" +
        "3. Confirmez l'installation"
      );
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't show if not Android, already installed, or prompt dismissed
  if (!isAndroid || isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
      <div className="max-w-lg mx-auto bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <Smartphone className="w-7 h-7 text-white" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-lg">
                Installer CCI Togo
              </h3>
              <p className="text-slate-300 text-sm mt-1">
                Installez l&apos;application pour un accès rapide et une meilleure expérience
              </p>
            </div>

            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 text-slate-400 hover:text-white transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-2 mt-3 mb-4">
            <span className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded-full">
              📱 Accès hors ligne
            </span>
            <span className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded-full">
              ⚡ Chargement rapide
            </span>
            <span className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded-full">
              🔔 Notifications
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="flex-1 text-slate-300 hover:text-white hover:bg-slate-700/50"
            >
              Plus tard
            </Button>
            <Button
              onClick={handleInstall}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Installer
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};
